import { useCallback, useEffect, useRef, useState } from "react";

interface UseInlineEditorOptions<T> {
  /** Current persisted value */
  value: T | null | undefined;
  /** Save handler. Throw to keep the editor open with an error message. */
  onSave: (value: T | null) => void | Promise<void>;
  /** Transform persisted value to edit state when opening editor */
  toEditState: (value: T | null | undefined) => Record<string, string>;
  /** Transform edit state to persisted value. Throw to reject with an error message. */
  fromEditState: (state: Record<string, string>) => T | null;
  /** Start in edit mode immediately */
  initialEdit?: boolean;
  /** Override dismiss behavior (escape/blur). When set, the editor won't close itself. */
  onCancel?: () => void;
}

/**
 * Shared hook for inline editor components.
 * Handles edit/save state, keyboard shortcuts, container blur detection,
 * and validation errors (via thrown errors in fromEditState or onSave).
 */
export function useInlineEditor<T>({
  value,
  onSave,
  toEditState,
  fromEditState,
  initialEdit,
  onCancel,
}: UseInlineEditorOptions<T>) {
  const [editing, setEditing] = useState(initialEdit ?? false);
  const [editState, setEditState] = useState<Record<string, string>>(() => (initialEdit ? toEditState(value) : {}));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const primaryInputRef = useRef<HTMLInputElement | null>(null);

  const startEditing = useCallback(() => {
    setEditing(true);
    setEditState(toEditState(value));
  }, [value, toEditState]);

  const dismiss = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      setEditing(false);
      setEditState({});
    }
  }, [onCancel]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const newValue = fromEditState(editState);
      await onSave(newValue);
      dismiss();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setSaving(false);
    }
  }, [editState, fromEditState, onSave, dismiss]);

  useEffect(() => {
    if (editing) {
      const timer = setTimeout(() => {
        primaryInputRef.current?.focus();
        primaryInputRef.current?.select();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [editing]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        dismiss();
      } else if (e.key === "Enter") {
        e.preventDefault();
        save();
      }
    },
    [dismiss, save],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      if (saving) return;
      if (containerRef.current?.contains(e.relatedTarget as Node)) return;
      dismiss();
    },
    [saving, dismiss],
  );

  const setField = useCallback((field: string, value: string) => {
    setError(null);
    setEditState((prev) => ({ ...prev, [field]: value }));
  }, []);

  return {
    editing,
    saving,
    error,
    editState,
    containerRef,
    primaryInputRef,
    startEditing,
    save,
    handleKeyDown,
    handleBlur,
    setField,
  };
}
